import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../lib/apiClient.js";
import { AnimalCarrouselCard } from "./AnimalCarrouselCard.js";
import LoadingPaw from "../../../Components/LoadingPaw.js";
import React from "react";

// Minimal animal shape used by the carousel
interface Animal {
  id: string;
  name: string;
  type: "cao" | "gato";
  breed?: string;
  gender?: "MACHO" | "FEMEA";
  photo_url?: string;
  image?: string;
  likes?: number;
  viewerHasWishlisted?: boolean;
}
interface ApiAnimal extends Partial<Animal> {
  id: string;
}


type CardPosition = "far-left" | "left" | "center" | "right" | "far-right";
interface CarouselCachePayload {
  animals: Animal[];
  fetchedAt: number;
}

const ANIMAL_CAROUSEL_QUERY = "/api/animals/featured";
const ANIMAL_CAROUSEL_CACHE_TTL_MS = 5 * 60 * 1000;
const ANIMAL_CAROUSEL_FETCH_COOLDOWN_MS = 15 * 1000;
const ANIMAL_CAROUSEL_CACHE_KEY = "landing:animal-carousel:featured";

const AnimalCarrousel = () => {
  const { t } = useTranslation(["landing", "common"]);
  const navigate = useNavigate();
  const [cards, setCards] = useState<Animal[]>([]);
  const [activeCardIndex, setActiveCardIndex] = useState<number>(3);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoScrollReleaseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchInFlightRef = useRef(false);
  const lastFetchAtRef = useRef(0);
  const isAutoScrollingRef = useRef(false);
  const lastCenteredIndexRef = useRef<number | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const applyAnimals = (animals: Animal[]) => {
      const centerIndex = Math.max(0, Math.floor((animals.length - 1) / 2));
      setActiveCardIndex(centerIndex);
      setCards(animals);
    };

    const readCache = (): CarouselCachePayload | null => {
      try {
        const raw = sessionStorage.getItem(ANIMAL_CAROUSEL_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CarouselCachePayload;
        if (!Array.isArray(parsed.animals) || typeof parsed.fetchedAt !== "number") {
          return null;
        }
        return parsed;
      } catch {
        return null;
      }
    };

    const writeCache = (animals: Animal[]) => {
      try {
        const payload: CarouselCachePayload = {
          animals,
          fetchedAt: Date.now(),
        };
        sessionStorage.setItem(ANIMAL_CAROUSEL_CACHE_KEY, JSON.stringify(payload));
      } catch {
        // Ignore storage failures (e.g., quota/privacy mode)
      }
    };

    const fetchAnimals = async () => {
      const now = Date.now();
      const cached = readCache();
      const cacheIsFresh =
        !!cached && now - cached.fetchedAt < ANIMAL_CAROUSEL_CACHE_TTL_MS;

      if (cacheIsFresh) {
        applyAnimals(cached.animals);
        setIsLoading(false);
        return;
      }

      if (fetchInFlightRef.current) return;
      if (now - lastFetchAtRef.current < ANIMAL_CAROUSEL_FETCH_COOLDOWN_MS) {
        if (cached) {
          applyAnimals(cached.animals);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(!cached);
      fetchInFlightRef.current = true;
      lastFetchAtRef.current = now;
      try {
        const resp = await apiClient.get<{ animals?: Animal[] }>(ANIMAL_CAROUSEL_QUERY);
        const rawAnimals = (resp?.animals || []) as ApiAnimal[];
        const finalAnimals: Animal[] = rawAnimals
          .filter((animal) => !!animal?.id)
          .map((animal) => ({
            id: animal.id,
            name: animal.name?.trim() || t("carousel.noName"),
            type: animal.type === "gato" ? "gato" : "cao",
            breed: animal.breed,
            gender:
              animal.gender === "MACHO" || animal.gender === "FEMEA"
                ? animal.gender
                : undefined,
            photo_url: animal.photo_url,
            image: animal.image,
            likes: animal.likes,
            viewerHasWishlisted: animal.viewerHasWishlisted,
          }));
        writeCache(finalAnimals);
        if (!isCancelled) {
          applyAnimals(finalAnimals);
        }
      } catch (error) {
        console.error("Error fetching animals:", error);
      } finally {
        fetchInFlightRef.current = false;
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchAnimals();

    return () => {
      isCancelled = true;
    };
  }, []);

  const getCardPosition = (index: number): CardPosition => {
    const positions: CardPosition[] = [
      "far-left",
      "left",
      "center",
      "right",
      "far-right",
    ];
    const relativePosition = (index - activeCardIndex + 5) % 5;
    return positions[relativePosition];
  };

  const handleCardClick = (index: number): void => {
    const distance = Math.abs(index - activeCardIndex);
    if (distance <= 1 || distance === 4) {
      setActiveCardIndex(index);
    }
  };

  const navigateToAnimal = (animal: Animal): void => {
    navigate(`/${animal.type}/${animal.name}-${animal.id}`);
  };

  const handleMobileCardClick = (index: number): void => {
    const animal = cards[index];
    if (!animal) return;

    if (index === activeCardIndex) {
      navigateToAnimal(animal);
      return;
    }

    handleCardClick(index);
  };

  const handleNextCard = (): void => {
    setActiveCardIndex((prevIndex) => (prevIndex + 1) % cards.length);
  };

  const handlePrevCard = (): void => {
    setActiveCardIndex(
      (prevIndex) => (prevIndex - 1 + cards.length) % cards.length,
    );
  };

  // Auto-center function for mobile scroll
  const autoCenter = () => {
    if (!scrollContainerRef.current || cards.length === 0) return;

    const container = scrollContainerRef.current;
    const cardWidth = 320 + 24; // card width + gap
    const scrollLeft = container.scrollLeft;

    // Calculate which card should be centered
    const cardIndex = Math.max(
      0,
      Math.min(cards.length - 1, Math.round(scrollLeft / cardWidth)),
    );
    const targetScrollLeft = cardIndex * cardWidth;
    const scrollDelta = Math.abs(targetScrollLeft - scrollLeft);

    if (lastCenteredIndexRef.current !== cardIndex) {
      setActiveCardIndex(cardIndex);
      lastCenteredIndexRef.current = cardIndex;
    }

    // Avoid scroll feedback loops by only re-centering when offset is meaningful.
    if (scrollDelta > 4) {
      isAutoScrollingRef.current = true;
      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });

      if (autoScrollReleaseRef.current) {
        clearTimeout(autoScrollReleaseRef.current);
      }
      autoScrollReleaseRef.current = setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 220);
    }
  };

  // Handle scroll events on mobile
  const handleScroll = () => {
    if (isAutoScrollingRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(autoCenter, 150);
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (autoScrollReleaseRef.current) {
        clearTimeout(autoScrollReleaseRef.current);
      }
    };
  }, []);

  return (
    <div className="relative z-20 w-full flex items-center justify-center">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-4 h-[550px]">
          <LoadingPaw
            size="md"
            message={t("carousel.loading")}
          />
        </div>
      ) : (
        <>
          {/* Desktop View - 3D Carousel */}
          <div className="hidden z-50 md:flex relative w-full h-[550px] items-center justify-center">
            <div
              className="relative w-full h-full"
              style={{ perspective: "1200px" }}
            >
              {cards.map((animal, index) => (
                <AnimalCarrouselCard
                  key={animal.id}
                  animal={animal}
                  position={getCardPosition(index)}
                  active={index === activeCardIndex}
                  onClick={() => handleCardClick(index)}
                  likeCount={animal.likes || 0}
                />
              ))}
            </div>

            {/* Desktop arrows - positioned closer with same style as testimonials */}
            <button
              onClick={handlePrevCard}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black border-none w-12 h-12 rounded-full flex items-center justify-center cursor-pointer shadow-md z-30 transition-all duration-300 hover:bg-gray-800 hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-golden focus:ring-offset-2"
              aria-label={t("carousel.previous")}
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
                aria-hidden="true"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              onClick={handleNextCard}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black border-none w-12 h-12 rounded-full flex items-center justify-center cursor-pointer shadow-md z-30 transition-all duration-300 hover:bg-gray-800 hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-golden focus:ring-offset-2"
              aria-label={t("carousel.next")}
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
                aria-hidden="true"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* Mobile View - Horizontal Scroll */}
          <div className="md:hidden w-full">
            <div
              ref={scrollContainerRef}
              className="overflow-x-auto scrollbar-hide py-8"
              onScroll={handleScroll}
              style={{ scrollSnapType: "x mandatory" }}
            >
              <div className="flex gap-6 px-6 min-w-max">
                {cards.map((animal, index) => (
                  <div
                    key={animal.id}
                    className={`flex-shrink-0 relative transition-all duration-300 ${
                      index === activeCardIndex
                        ? "scale-100 opacity-100"
                        : "scale-90 opacity-80"
                    }`}
                    style={{
                      width: "320px",
                      height: "450px",
                      scrollSnapAlign: "center",
                    }}
                    onClick={() => handleMobileCardClick(index)}
                  >
                    <div
                      className="w-full h-full rounded-2xl bg-cover bg-center bg-no-repeat border-4 border-white shadow-xl cursor-pointer relative overflow-hidden"
                      style={{
                        backgroundImage: animal.photo_url
                          ? `url(${animal.photo_url})`
                          : animal.image
                            ? `url(${animal.image})`
                            : undefined,
                      }}
                    >
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                      {/* Card content - simplified */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                        <p className="uppercase text-xs font-medium tracking-wide text-white/80 mb-1">
                          {animal.gender === "MACHO"
                            ? t("common:animal.male")
                            : animal.gender === "FEMEA"
                              ? t("common:animal.female")
                              : animal.gender}
                        </p>
                        <h2 className="text-xl font-bold text-white mb-2">
                          {animal.name}
                        </h2>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile dots indicator */}
            <div
              className="flex justify-center gap-2 mt-4"
              role="tablist"
              aria-label={t("carousel.indicators")}
            >
              {cards.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCardIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-golden focus:ring-offset-1 ${
                    index === activeCardIndex
                      ? "bg-white"
                      : "bg-white bg-opacity-40"
                  }`}
                  role="tab"
                  aria-selected={index === activeCardIndex}
                  aria-label={t("carousel.viewAnimal", { index: index + 1 })}
                  type="button"
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnimalCarrousel;
